import { useEffect, useRef } from "react"
import { useNavigationContext } from "@kit/design/navigation"
import { MODIFIER_KEY, hasModifierKey } from "@kit/design/utils/platform"

/** Keys for the hint on `Navigation.Trigger`, matching the binding below. */
export const COLLAPSE_SHORTCUT_KEYS = [MODIFIER_KEY, "\\"]

/**
 * Binds Cmd/Ctrl+\ to toggle the sidebar, following Notion and Figma.
 *
 * Reads the collapsed state from `Navigation` context, so it has to be called
 * from a component *inside* a `Navigation` — see `NavCollapseShortcut`.
 *
 * No "is the user typing?" guard: `mod+\` collides with nothing in the editor's
 * marks plugin, and toggling the nav mid-compose is a deliberate act. Inert on
 * mobile, where `Navigation.Trigger` is hidden and the sidebar is an overlay.
 */
export function useNavigationToggleHotkey() {
  const { collapsed, setCollapsed, mobile } = useNavigationContext()
  // Ref so the single listener sees current state without re-binding, matching
  // useCommandPaletteHotkeys.
  const toggleRef = useRef(null)
  toggleRef.current = () => setCollapsed(!collapsed)

  useEffect(() => {
    if (mobile) return undefined

    const handleKeyDown = event => {
      // Held keys auto-repeat, which would flip the sidebar dozens of times.
      if (event.repeat) return
      if (!hasModifierKey(event) || event.key !== "\\") return
      event.preventDefault()
      toggleRef.current()
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [mobile])
}

/**
 * Mount point for `useNavigationToggleHotkey`. Renders nothing and exists only
 * to be a child: `AppNavigation` is the component that renders `Navigation`, so
 * it can't call the hook itself — the context isn't available until its own
 * render has returned.
 */
export function NavCollapseShortcut() {
  useNavigationToggleHotkey()
  return null
}
